import React, { useContext, useEffect, useState } from "react";
import { Button, Form, Row, Col } from "react-bootstrap";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useSyncedField } from "../../components/Sidebar";
import SidebarContext from "../../components/Sidebar/SidebarContext";

// parameters.headers shape: { [headerName]: value }

const COMMON_HEADERS = [
    "Authorization",
    "Accept",
    "Content-Type",
    "Content-Length",
    "X-API-Key",
    "X-Version",
    "X-Request-ID",
    "X-Correlation-ID",
    "X-Forwarded-For",
    "User-Agent",
    "Accept-Encoding",
    "Cache-Control",
    "Access-Control-Allow-Origin",
    "Access-Control-Allow-Headers",
    "Access-Control-Allow-Methods",
];

function headersObjToRows(obj) {
    if (!obj || typeof obj !== "object") return [];
    return Object.entries(obj).map(([key, value]) => ({ key, value: String(value) }));
}

function rowsToHeadersObj(rows) {
    const obj = {};
    for (const { key, value } of rows) {
        if (key.trim()) obj[key.trim()] = value;
    }
    return obj;
}

const WebhookHeaders = () => {
    const { entity } = useContext(SidebarContext);
    const [parameters, setParameters] = useSyncedField("parameters", {});

    const [rows, setRows] = useState(() =>
        headersObjToRows(entity?.parameters?.headers),
    );

    // Sync rows → parameters.headers whenever rows change
    useEffect(() => {
        setParameters({
            ...(parameters ?? {}),
            headers: rowsToHeadersObj(rows),
        });
    }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset when entity changes
    useEffect(() => {
        setRows(headersObjToRows(entity?.parameters?.headers));
    }, [entity?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const addRow = () => setRows((prev) => [...prev, { key: "", value: "" }]);
    const removeRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));
    const updateKey = (i, key) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, key } : r)));
    const updateValue = (i, value) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, value } : r)));

    return (
        <div className="p-3">
            <p className="text-muted small mb-3">
                These headers are added to every webhook request.
            </p>

            {rows.map((row, index) => (
                <Row key={index} className="mb-2 align-items-center g-2">
                    <Col xs={5}>
                        <Autocomplete
                            freeSolo
                            options={COMMON_HEADERS}
                            value={row.key}
                            onInputChange={(_, newVal) => updateKey(index, newVal)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    placeholder="Header name"
                                    variant="outlined"
                                />
                            )}
                        />
                    </Col>
                    <Col xs={6}>
                        <Form.Control
                            size="sm"
                            value={row.value}
                            onChange={(e) => updateValue(index, e.target.value)}
                            placeholder="Value"
                        />
                    </Col>
                    <Col xs={1}>
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeRow(index)}
                            title="Remove"
                        >
                            <i className="fa fa-times" />
                        </Button>
                    </Col>
                </Row>
            ))}

            <Button variant="outline-primary" size="sm" onClick={addRow} className="mt-2">
                <i className="fa fa-plus me-1" />
                Add Header
            </Button>
        </div>
    );
};

export default WebhookHeaders;
